import { ChevronDown, ChevronRight, LineChart } from "lucide-react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { TruncatedText } from "@/components/ui/truncated-text";
import type { DailyRanksTheme, MetricKey } from "@/types/rankTracker";
import { KpsBreakdownPopover } from "@/components/keyword-bank/KpsBreakdownPopover";
import { buildKpsRollUpViewModel } from "../kpsViewModel";
import { RankHeatCell } from "./RankHeatCell";
import type { RankType } from "./RankTypeToggle";
import { METRIC_CELL_TOOLTIPS, formatDateAdded, formatMetricValue } from "./constants";

export interface ThemeRowProps {
  theme: DailyRanksTheme;
  /** `days` from the daily-ranks response -- one heat cell per entry, the same array the product
   * row above it and the keyword rows below it are built from. */
  days: string[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  /** `true`/`false`/`"indeterminate"` -- the theme checkbox supports the `Checkbox` primitive's
   * indeterminate state for a partial selection (`components/rank-tracker.md § Target state -- the
   * screen`). Selection state itself is WEB-009's; this row only renders what it is told. */
  selected?: boolean | "indeterminate";
  onSelectAllChange?: (selected: boolean) => void;
  /**
   * When a filter has narrowed which of this theme's keywords are visible, the count beside the
   * label reads `N of M keywords` instead of `(M keywords)` -- WEB-009's filter wires this in; until
   * then every theme row shows the full count.
   */
  visibleKeywordCount?: number;
  /**
   * WEB-004 / FR-7. A theme row always shows its **organic** roll-up rank — never a sponsored one.
   * In Sponsored view (`"sponsored"`) its day cells dim to 35% opacity; Organic and Both views
   * render them exactly as today. */
  rankType?: RankType;
  /** Fired by the chart-icon button in this row's chart cell -- WEB-006. `RankHeatmapTable`
   * constructs the `level: 'theme'` `RankDetailTarget` arm (this row's own `product` and `theme`,
   * no keyword) around this call; this row only exposes the affordance, the same shape as
   * `KeywordRow`'s `onOpenDetail`. */
  onOpenDetail?: () => void;
}

/**
 * One of the five roll-up metric cells (`KPS` · `Search Vol.` · `Kw. Sales` · `Comp.` · `Rel.`).
 * Renders `metrics[metric].value` via the same `formatMetricValue` a keyword row uses, and carries
 * `METRIC_CELL_TOOLTIPS[metric]`'s per-cell tooltip (FR-6) -- the roll-up heat cell beside it is the
 * precedent for the affordance, not an implementation this extends: these cells rendered plain text
 * before WEB-002. `keywordCount` is always the row's own wire count (`theme.keywordCount` here),
 * never a filtered/visible count -- see `constants.ts`' `METRIC_CELL_TOOLTIPS` docblock.
 */
function ThemeMetricCell({
  metric,
  rollUp,
  keywordCount,
}: {
  metric: MetricKey;
  rollUp: { value: number | null; keywordCount: number };
  keywordCount: number;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <TableCell className="py-2.5 px-3 text-right text-muted-foreground tabular-nums">
          {formatMetricValue(rollUp.value)}
        </TableCell>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-left">
        {METRIC_CELL_TOOLTIPS[metric](rollUp.keywordCount, keywordCount)}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The `KPS` roll-up cell specifically -- same value and tooltip as `ThemeMetricCell` above, plus
 * the breakdown control (FR-8). Kept separate from `ThemeMetricCell` because the other four roll-up
 * metrics carry no breakdown at all; sharing one generic cell for all five would mean threading an
 * optional trigger through a component that has no use for it in four of its five call sites.
 */
function ThemeKpsMetricCell({ theme }: { theme: DailyRanksTheme }) {
  const rollUp = theme.metrics.kps;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <TableCell className="py-2.5 px-3 text-right text-muted-foreground tabular-nums">
          <div className="flex items-center justify-center gap-1.5">
            {formatMetricValue(rollUp.value)}
            {/* Stops the click from reaching the row's own onClick, which toggles expand/collapse
                (FR-11) -- same pattern as the checkbox and expand button below. */}
            <span onClick={(e) => e.stopPropagation()}>
              <KpsBreakdownPopover viewModel={buildKpsRollUpViewModel(theme.name, theme.metrics, theme.kpsRollUp)} />
            </span>
          </div>
        </TableCell>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-left">
        {METRIC_CELL_TOOLTIPS.kps(rollUp.keywordCount, theme.keywordCount)}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The theme row: label (the theme's own `name` -- the "No theme" bucket is already named that on
 * the wire, so this renders it with no special-casing), the chart-icon button (WEB-006), the five
 * roll-up metric cells (FR-4), its own `Date Added` (the date this theme was assigned to the
 * product, or the absence mark -- AUT-382), and one roll-up heat cell per day.
 */
export function ThemeRow({
  theme,
  days,
  isExpanded,
  onToggleExpand,
  selected = false,
  onSelectAllChange,
  visibleKeywordCount,
  rankType = "organic",
  onOpenDetail,
}: ThemeRowProps) {
  const countLabel =
    visibleKeywordCount !== undefined
      ? `${visibleKeywordCount} of ${theme.keywordCount} keywords`
      : `(${theme.keywordCount} keyword${theme.keywordCount !== 1 ? "s" : ""})`;

  return (
    <TableRow className="cursor-pointer" onClick={onToggleExpand}>
      {/* w-[340px] to match the header/product-row column width (item 9); py-2.5 trims this row to
          the mockup's measured 41px alongside RankHeatCell's own trimmed padding -- no explicit row
          height needed. Left white (bg-card, the sticky column's own default) -- only a product
          row's label cell is tinted. */}
      <TableCell sticky className="w-[340px] py-2.5">
        {/* AUT-524: the nested-drawer accent bar (WEB-010). Anchored to the sticky `<td>` itself --
            a positioned element -- with `!absolute -inset-y-px`, so it spans the FULL row height and
            butts into the next row's bar as one continuous line. The old span was anchored to the
            inner flex div, which the cell's padding insets, so on rows taller than its `-inset-y-3`
            overhang it stopped short and the bar read as a column of dashes (AUT-524). `!absolute`
            overrides the sticky column's `[&>*]:relative` rule; `left-5` sits in the `pl-4` gutter,
            left of the checkbox; `-inset-y-px` bridges the row's 1px bottom border. */}
        <span aria-hidden className="pointer-events-none !absolute -inset-y-px left-5 w-0.5 bg-primary" />
        <div className="relative flex items-center gap-2 min-w-0 pl-4">
          <span onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={selected}
              onCheckedChange={(checked) => onSelectAllChange?.(!!checked)}
              aria-label={`Select all keywords in ${theme.name}`}
            />
          </span>

          <button
            type="button"
            aria-label={isExpanded ? `Collapse ${theme.name}` : `Expand ${theme.name}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            className="shrink-0 text-muted-foreground"
          >
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>

          <span className="flex items-center gap-1 min-w-0">
            <TruncatedText className="font-medium">{theme.name}</TruncatedText>
            <span className="shrink-0 text-xs text-muted-foreground">{countLabel}</span>
          </span>

          {/* WEB-002: the roll-up Ad badge. Derived from the keywords actually present after
              filtering (`.some(k => k.hasAdSpend)`), matching the prototype -- so selecting
              "Not advertised" hides it with its advertised keywords, rather than reading the
              server's full-set `theme.hasAdSpend` (which stays true). Equivalent to the server
              roll-up when unfiltered. Sponsored-Products accent colour, as the keyword row. */}
          {theme.keywords.some((keyword) => keyword.hasAdSpend) && (
            <Badge
              variant="secondary"
              className="shrink-0 cursor-default bg-[hsl(var(--heat-sp-4))] text-[hsl(var(--heat-sp-on-light))] hover:bg-[hsl(var(--heat-sp-4))]"
              title="Ad spend on one or more keywords"
              data-testid="ad-badge"
            >
              Ad
            </Badge>
          )}
        </div>
      </TableCell>

      {/* Chart column -- same px-0/text-center cell shape as KeywordRow's (COL_CHART is only 40px
          wide), the same LineChart icon, h-6 w-6 hit area, hover treatment and stopPropagation
          (KeywordRow.tsx:78) so the click opens the detail view without also toggling this row's
          own expand/collapse. */}
      <TableCell className="py-2.5 px-0 text-center">
        <button
          type="button"
          aria-label={`View rank chart for ${theme.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetail?.();
          }}
          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-primary transition-colors hover:bg-muted"
        >
          <LineChart className="h-4 w-4" />
        </button>
      </TableCell>

      <ThemeKpsMetricCell theme={theme} />
      <ThemeMetricCell
        metric="searchVolume"
        rollUp={theme.metrics.searchVolume}
        keywordCount={theme.keywordCount}
      />
      <ThemeMetricCell
        metric="keywordSales"
        rollUp={theme.metrics.keywordSales}
        keywordCount={theme.keywordCount}
      />
      <ThemeMetricCell
        metric="competitors"
        rollUp={theme.metrics.competitors}
        keywordCount={theme.keywordCount}
      />
      <ThemeMetricCell
        metric="relevance"
        rollUp={theme.metrics.relevance}
        keywordCount={theme.keywordCount}
      />

      {/* AUT-497 slice B: the Ad push column. A theme row renders an empty cell (not a pill, not
          `–`, not a roll-up) -- a per-keyword ad-push suggestion does not exist for a roll-up row
          (Decision D-4). The column's 140px width comes from the header row under `table-fixed`. */}
      <TableCell />

      {days.map((day, i) => (
        <RankHeatCell
          key={day}
          day={day}
          position={theme.positions[i]}
          variant="rollup"
          keywordCount={theme.keywordCount}
          count={theme.counts[i]}
          dim={rankType === "sponsored"}
        />
      ))}

      <TableCell className="py-2.5 px-3 text-left text-muted-foreground whitespace-nowrap border-l border-border">
        {formatDateAdded(theme.dateAdded)}
      </TableCell>
    </TableRow>
  );
}
